import React from 'react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-background">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-900/20 via-background to-background">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-foreground mb-6">Terms & Conditions</h1>

          <div className="prose prose-lg max-w-none space-y-8 text-muted-foreground">
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">1. Agreement to Terms</h2>
              <p>
                The following terms and conditions apply to all services undertaken by ALM Group of Companies. 
                By accessing or using the services provided by ALM Group of Companies ("Company," "we," "us," or "our"), 
                you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not 
                use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">2. Services</h2>
              <p className="mb-4">
                ALM Group of Companies provides the following services through its divisions:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>ALM Trading LLC:</strong> International trade, import/export, and logistics services</li>
                <li><strong>ALM Consultants LLC:</strong> Business and management consulting services</li>
                <li><strong>ALM IT Services:</strong> IT infrastructure and support services</li>
                <li><strong>ALM Software Services:</strong> Custom software development and technology solutions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">3. Revision Policy</h2>
              <p>
                Revisions are contingent on the service package selected. Clients can approach us for amendments, 
                and we will revise their deliverables with charges based on the agreed terms, given that the scope 
                and concept remain intact. The revision turnaround time will be 48 hours for most services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">4. Payment Terms</h2>
              <p className="mb-4">For services requiring payment:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>All fees are in US Dollars unless otherwise specified</li>
                <li>Payment is due as agreed in the service contract or invoice</li>
                <li>Late payments may incur additional fees</li>
                <li>We accept credit cards, debit cards, and bank transfers</li>
                <li>For ongoing services, payment terms are outlined in the service agreement</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Refund and Cancellation Policy</h2>
              <p className="mb-4">
                ALM Group of Companies is committed to client satisfaction. Our refund policy varies by service division:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Service cancellations must be requested in writing</li>
                <li>Refunds, if applicable, will be processed within 30 business days</li>
                <li>Custom development work and consulting services may have specific refund terms outlined in contracts</li>
                <li>For trading services, cancellation fees may apply based on the stage of shipment/logistics</li>
                <li>Software development projects: Refunds available only before initial concept delivery</li>
                <li>IT Services: Monthly subscriptions can be cancelled with 30 days notice</li>
              </ul>
              <p className="mt-4">
                We won't entertain refund requests if the client stays non-responsive for 45 days; however, 
                clients can email us to pause the project if there are legitimate circumstances.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Client Responsibilities</h2>
              <p className="mb-4">You agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate and timely information required for service delivery</li>
                <li>Respond to communications within reasonable timeframes</li>
                <li>Make payments according to agreed terms</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Not use our services for fraudulent or illegal purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Intellectual Property</h2>
              <p>
                All content, materials, and services provided by ALM Group of Companies are protected by copyright, 
                trademark, and other intellectual property laws. Upon full payment for custom development services, 
                ownership rights transfer to the client as specified in the service agreement. For consulting services, 
                deliverables and methodologies remain the property of ALM Group of Companies unless otherwise specified.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Confidentiality</h2>
              <p>
                We respect the confidentiality of your business information. Both parties agree to maintain 
                confidentiality of proprietary information shared during the course of service delivery. Standard 
                non-disclosure agreements are available upon request.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">9. Quality Assurance</h2>
              <p>
                ALM Group of Companies is committed to delivering high-quality services. We conduct thorough 
                quality checks and ensure all deliverables meet industry standards. Our team will not deviate 
                from specifications provided by the client without prior approval.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">10. Delivery Policy</h2>
              <p className="mb-4">Standard delivery times by service:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Software Development:</strong> 10-14 weeks for standard projects</li>
                <li><strong>Consulting Services:</strong> As per project scope and timeline</li>
                <li><strong>IT Infrastructure Setup:</strong> 2-4 weeks depending on complexity</li>
                <li><strong>Trading/Logistics:</strong> Based on shipping routes and customs clearance</li>
              </ul>
              <p className="mt-4">
                Rush delivery options are available for an additional fee. Contact our support team for expedited service requests.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">11. Warranties and Disclaimers</h2>
              <p className="mb-4">
                We strive to provide high-quality services, however:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Services are provided with industry-standard warranties</li>
                <li>We do not guarantee specific business results or outcomes</li>
                <li>We are not liable for delays caused by circumstances beyond our control</li>
                <li>Software and IT services may experience occasional maintenance downtime</li>
                <li>Trading services are subject to international regulations and customs procedures</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">12. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, ALM Group of Companies shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages arising from your use of our services. Our total 
                liability shall not exceed the amount paid by you for the specific service in question.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">13. Customer Support</h2>
              <p>
                We offer 24-hour customer support to address your questions and queries across all service divisions. 
                You can contact us at any time through:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Email: info@almgroupofcompanies.com</li>
                <li>Phone: +1 (555) 123-4567</li>
                <li>Office: 650 California St, San Francisco, CA 94108</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">14. Communication Policy</h2>
              <p>
                You agree that ALM Group of Companies is not responsible for any communication from email addresses 
                other than those under our official domain (@almgroupofcompanies.com) or phone numbers not specified 
                on our website. We are not liable for any damages caused by fraudulent communications impersonating our company.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">15. Termination</h2>
              <p>
                Either party may terminate services with written notice as specified in the service agreement. 
                ALM Group of Companies reserves the right to terminate services immediately if:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Payment terms are violated</li>
                <li>Client provides false or misleading information</li>
                <li>Client uses services for illegal purposes</li>
                <li>Client breaches confidentiality agreements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">16. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of California, 
                United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">17. Dispute Resolution</h2>
              <p>
                Any disputes arising from these Terms or our services shall first be resolved through good faith 
                negotiation. If negotiation fails, disputes shall be resolved through binding arbitration in 
                San Francisco, California, in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">18. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of any material changes 
                via email or website notice. Your continued use of our services after changes are posted constitutes 
                acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">19. Entire Agreement</h2>
              <p>
                These Terms and Conditions, together with any service agreements and our Privacy Policy, constitute 
                the entire agreement between you and ALM Group of Companies regarding the use of our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">20. Contact Information</h2>
              <p>
                If you have any questions about these Terms and Conditions, please contact ALM Group of Companies:
              </p>
              <div className="mt-4 p-6 bg-card border border-border rounded-lg">
                <p><strong>ALM Group of Companies</strong></p>
                <p>Email: info@almgroupofcompanies.com</p>
                <p>Phone: +1 (555) 123-4567</p>
                <p>Address: 650 California St, San Francisco, CA 94108</p>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
